export function buildTCO(rows) {
  if (!Array.isArray(rows)) return [];

  return rows
    .filter((r) => getVendor(r))
    .map((r) => {
      const vendor = getVendor(r);
      const brand = r.brand;
      const category = r.category || "NA";

      const quantity = safeNum(r.quantity ?? r.Quantity);
      const unitPrice = safeNum(r.unitprice ?? r.unitPrice ?? r.unit_price);

      const base = calculateBase([r]);
      const gst = sumGST([r]);

      // ---------- LOGISTICS ----------
      const logistics = calculateLogistics(base, r.logistics);

      const freight = calculateFreight(base, r.freight);

      // ---------- IMPLEMENTATION ----------
      const implementation = resolveValue({
        raw: r.implementation,
        fallback: calculateImplementation([r]),
      });

      // ---------- TRAINING ----------
      const training = resolveValue({
        raw: r.training,
        fallback: safeNum(r.training_cost),
      });

      // ---------- AMC (5 YEARS) ----------
      const amc = hasValidValue(r.amc)
        ? extractTotalForYears(r.amc, 5)
        : calculate5YearAMC([r]);

      // ---------- SUPPORT ----------
      const support9x5 = hasValidValue(r["support 9x5"])
        ? extractTotalForYears(r["support 9x5"], 5)
        : calculate5YearSupport([r]);

      const support24x7 = hasValidValue(r["support 24x7"])
        ? round(extractYearlyCost(r["support 24x7"]) * 5)
        : round(support9x5 * 1.6);

      const postGoLive = round(base * 0.02 * 5);

      // ---------- PAYMENT ----------
      const paymentTerms = r.paymentterms ?? r.paymentTerms ?? "";

      // Extract payment terms first
      const { advancePct, creditDays, hasLC } = extractPaymentTerms(
        r.paymentterms,
        r.lc,
        r["finance cost"]
      );

      // fallback credit source
      const fallbackCredit = r["payment credit period"] || r["finance cost"];
      console.log(fallbackCredit)

      // Finance calculation
      const financeText = r["finance cost"];
      const financeStr = String(financeText || "").toLowerCase();

      const finance =
        hasValidValue(financeText) && !financeStr.includes("day")
          ? extractNumber(financeText)
          : calculateFinance(
              base,
              r.paymentterms,
              creditDays || fallbackCredit,
            );

      const advancePayment = hasValidValue(r["advance payment"])
        ? round((base * extractNumber(r["advance payment"])) / 100)
        : calculateAdvanceCost(base, advancePct);

      // ---------- WARRANTY ----------
      const warrantyYears = extractWarrantyYears([r]);
      const warrantyCost = calculateExtendedWarranty(base, warrantyYears);

      // LC cost
      const lcCost =
        hasLC && safeNum(base) > 0 ? round(safeNum(base) * 0.01) : 0;

      // ---------- RISK + FOC ----------
      const risk = calculateRisk([r], base);
      const focBenefits = calculateFOC([r]);

      // consumables
      const consumables = calculateConsumables(base, r);


      const total =
        base +
        gst +
        logistics +
        freight +
        implementation +
        training +
        amc +
        support9x5 +
        support24x7 +
        postGoLive +
        finance +
        lcCost +
        warrantyCost +
        risk +
        consumables-
        focBenefits;

      return {
        vendor,
        itemName: r.item_name || r.model || "NA",
        brand,
        category,
        quantity,
        unitPrice,
  
        base,
        gst,
        logistics,
        freight,
        
        installation: implementation,
        training,

        advancePayment,
        creditDays: creditDays > 0 ? creditDays : "Not Applicable",
        finance,
        lcCost,
        hasLC,

        support9x5,
        support24x7,
        postGoLive,
        amc,

        warrantyCost,
        risk,
        consumables,
        focBenefits,

       

        total: round(total),
      };
    });
}

/* ------------ CORE CALCULATIONS ------------- */

function calculateBase(items) {
  return round(
    items.reduce((s, r) => {
      // const rawTotal = r.totalprice ?? r.totalPrice;

      // ✅ Use total price only if it's a valid number
      // if (rawTotal != null && safeNum(rawTotal) > 0) {
      //   return s + safeNum(rawTotal);
      // }

      const up = safeNum(r.unitprice ?? r.unitPrice ?? r.unit_price);
      const qty = safeNum(r.quantity ?? r.Quantity);

      return s + up * qty;
    }, 0),
  );
}

function sumGST(arr) {
  return round(
    arr.reduce((s, r) => {
      const rawTotal = r.totalprice ?? r.totalPrice;

      const price =
        rawTotal != null && safeNum(rawTotal) > 0
          ? safeNum(rawTotal)
          : safeNum(r.unitprice ?? r.unitPrice ?? r.unit_price) *
            safeNum(r.quantity ?? r.Quantity);

      const gst = extractGSTRate(r);
      return s + (price * gst) / 100;
    }, 0),
  );
}

/* ------------ LOGISTICS ------------- */

function calculateLogistics(base, raw) {
  if (!raw) return 0;

  const text = String(raw).toLowerCase();

  if (text.includes("included")) return 0;
  if (text.includes("extra") || text.includes("actual")) return 0;

  const percent = text.match(/(\d+(\.\d+)?)\s*%/);
  if (percent) {
    return round((base * parseFloat(percent[1])) / 100);
  }

  const value = extractNumber(raw);
  return value || 0;
}

// Freight

function calculateFreight(base, raw) {
  if (!raw) return 0;

  const text = String(raw).toLowerCase();

  if (text.includes("included")) return 0;
  if (text.includes("extra") || text.includes("actual")) return 0;

  const percent = text.match(/(\d+(\.\d+)?)\s*%/);
  if (percent) {
    return round((base * parseFloat(percent[1])) / 100);
  }

  const value = extractNumber(raw);
  return value || 0;
}

/* ------------ AMC (5 YEAR) ------------- */

function calculate5YearAMC(items) {
  const yearly = items.reduce((s, r) => s + safeNum(r.amc_cost), 0);
  return round(yearly * 5);
}

/* ------------ SUPPORT (5 YEAR) ------------- */

function calculate5YearSupport(items) {
  const yearly = items.reduce(
    (s, r) => s + safeNum(r.support_cost) + safeNum(r.sla_breach_penalty),
    0,
  );
  return round(yearly * 5);
}

/* ------------ IMPLEMENTATION ------------- */

function calculateImplementation(items) {
  return round(
    items.reduce(
      (s, r) =>
        s +
        safeNum(r.installation_cost) +
        safeNum(r.training_cost) +
        safeNum(r.integration_cost),
      0,
    ),
  );
}

/* ------------ FINANCE ------------- */

function calculateFinance(amount, terms, creditPeriod) {
  if (!amount) return 0;

  const roundSafe = (v) => Math.round(v || 0);

  const clean = (t) => String(t || "").toLowerCase();

  const text = clean(terms);
  const creditText = clean(creditPeriod);

  // If pure advance/cash payment → no finance cost
  if (
    text.includes("before dispatch") ||
    text.includes("proforma") ||
    (text.includes("cash") && !creditText)
  ) {
    // still check if credit period separately exists
    if (!creditText) return 0;
  }

  let days = 0;

  // --- 1. Prefer explicit credit period ---
 if (typeof creditPeriod === "number") {
  days = creditPeriod;
} else {
  const creditMatch = creditText.match(/(\d+)\s*day/);
  if (creditMatch) {
    days = parseInt(creditMatch[1], 10);
  }
}

  // --- 2. Else search in payment terms ---
  if (!days) {
    const matches = text.match(/(\d+)\s*day/g);
    if (matches && matches.length) {
      days = parseInt(matches[0], 10);
    }
  }

  if (!days || days <= 0) return 0;

  // 12% annual interest
  return roundSafe((amount * 0.12 * days) / 365);
}

/* ------------ RISK MODEL ------------- */

function calculateRisk(items, base) {
  let risk = 0;

  items.forEach((r) => {
    const del = String(r.delivery || "").toLowerCase();
    const pay = String(r.paymentterms ?? r.paymentTerms ?? "").toLowerCase();
    const war = String(r.warranty || "").toLowerCase();

    if (del.includes("transit")) risk += base * 0.01;
    if (pay.includes("advance") && pay.includes("100%")) risk += base * 0.01;

    if (!war || war === "na") risk += base * 0.005;
  });

  return round(risk);
}

/* ------------ FOC BENEFITS ------------- */

function calculateFOC(items) {
  return round(
    items.reduce((s, r) => {
      const focText = String(r.foc || "").toLowerCase();
      if (focText.includes("free")) s += safeNum(r.installation_cost);
      if (r.free_training) s += safeNum(r.training_cost);
      if (r.free_spares_value) s += safeNum(r.free_spares_value);
      return s;
    }, 0),
  );
}

/* ------------ WARRANTY GAP (CRITICAL) ------------- */

function extractWarrantyYears(items) {
  let years = 1;

  items.forEach((r) => {
    const match = String(r.warranty || "").match(/(\d+)\s*(year|yr)/i);
    if (match) years = Math.max(years, Number(match[1]));
  });

  return years;
}

function calculateExtendedWarranty(base, vendorYears) {
  if (vendorYears >= 5) return 0;
  const missing = 5 - vendorYears;
  return round(base * 0.03 * missing);
}

function extractPaymentTerms(text = "", lcField = "", financeText = "") {
  const t = String(text || "").toLowerCase();
  const lcText = String(lcField || "").toLowerCase();
  const finance = String(financeText || "").toLowerCase();

  const advanceMatch = t.match(/(\d+)\s*%?\s*advance/);
  const advancePct = advanceMatch ? Number(advanceMatch[1]) : 0;

  // credit from payment terms
  let creditMatch = t.match(/(\d+)\s*-?\s*day/);

  // fallback: finance cost text
  if (!creditMatch) {
    creditMatch = finance.match(/(\d+)\s*-?\s*day/);
  }

  const creditDays = creditMatch ? Number(creditMatch[1]) : 0;

  const hasLC =
    (t.includes("lc") ||
      t.includes("letter of credit") ||
      lcText.includes("applicable")) &&
    !lcText.includes("not") &&
    lcText !== "na" &&
    lcText !== "";

  return { advancePct, creditDays, hasLC };
}


function calculateAdvanceCost(base, pct) {
  if (!base || !pct) return 0;
  return round((base * pct * 0.12 * 30) / (100 * 365));
}

// dual mode

function resolveValue({ raw, fallback }) {
  return hasValidValue(raw) ? extractNumber(raw) : fallback;
}

/* ------------ HELPERS ------------- */

function hasValidValue(v) {
  if (v == null) return false;
  const t = String(v).toLowerCase().trim();
  return t !== "" && t !== "na" && t !== "n/a";
}

function extractNumber(v) {
  if (!hasValidValue(v)) return 0;

  const matches = String(v).match(/(\d[\d,]*(?:\.\d+)?)/g);
  if (!matches) return 0;

  // choose largest number (likely amount)
  return Math.max(...matches.map(safeNum));
}

function extractYearlyCost(v) {
  // handles "4000 for 5 years", "5 years : 7000"
  const total = extractNumber(v);
  const yearsMatch = String(v).match(/(\d+)\s*year/i);
  const years = yearsMatch ? Number(yearsMatch[1]) : 1;
  return years > 0 ? total / years : total;
}

function getVendor(row) {
  let vendor = row.vendorname || row.vendorName || row.supplier || null;

  // Controlled fallback to brand
  if (!vendor || String(vendor).toLowerCase() === "na") {
    vendor = row.brand;
  }

  // Final rejection
  const v = String(vendor || "")
    .toLowerCase()
    .trim();
  if (!v || v === "na" || v === "n/a") return null;

  return vendor;
}

function sum(arr, key) {
  return round(arr.reduce((s, r) => s + safeNum(r[key]), 0));
}

function safeNum(v) {
  if (v == null) return 0;
  const n = Number(String(v).replace(/[^0-9.]/g, ""));
  return isNaN(n) ? 0 : n;
}

function extractGSTRate(r) {
  if (r.gst != null) return safeNum(r.gst);
  if (r.gst_rate != null) return safeNum(r.gst_rate);

  const text = `${r.inclusions || ""} ${r.notes || ""}`.toLowerCase();
  const match = text.match(/gst\s*@?\s*(\d{1,2})/);

  return match ? Number(match[1]) : 0;
}

function round(v) {
  return Math.round(v * 100) / 100;
}

function resolvePercentOrAmount(raw, base, fallback = 0) {
  if (!hasValidValue(raw)) return fallback;

  const text = String(raw).toLowerCase();

  const num = extractNumber(text);

  // if explicitly percent OR small number -> treat as %
  if (text.includes("%") || num <= 100) {
    return round((base * num) / 100);
  }

  return num;
}
function extractTotalForYears(text, years) {
  const total = extractNumber(text);

  const match = String(text).match(/(\d+)\s*year/i);
  if (!match) return total;

  const providedYears = Number(match[1]);
  if (!providedYears) return total;

  return round((total / providedYears) * years);
}


function calculateConsumables(base, r) {
  const notes = String(r.notes || "").toLowerCase();

  // Explicitly included
  if (notes.includes("consumables included")) {
    return 0;
  }

  // Explicitly excluded
  if (
    notes.includes("consumables") &&
    (notes.includes("excluded") || notes.includes("exclusions"))
  ) {
    return round(base * 0.03 * 5);
  }

  return 0;
}
